import { useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Checkbox,
    List,
    ListItem,
    ListItemPrefix,
    Alert,
} from "@material-tailwind/react";
import { fetcherFull } from "utils/api";
import { toast } from "react-toastify";
import { MIN_SEAT_COUNT } from "utils/consts";
import { formatLongDate } from "utils/format-date";

const PendingDowngradeDialog = ({ open, onClose, users, subscription, userLimit, onSuccess }) => {
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const requiredRemovalCount = Math.abs(subscription.pending_seat_change);
    const newSeatCount = userLimit;  // Use validated backend value
    const effectiveDate = formatLongDate(subscription.pending_change_time);

    const handleUserToggle = (userId) => {
        if (selectedUserIds.includes(userId)) {
            setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
        } else {
            setSelectedUserIds([...selectedUserIds, userId]);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        const loadingToast = toast.loading("Processing user deactivations...");
        let successCount = 0;
        let errorCount = 0;

        for (const userId of selectedUserIds) {
            try {
                const response = await fetcherFull(`/v2/user/${userId}`, "PUT", {
                    active: true,
                    inactive_reason: "pending",
                });
                if (response && response.id) {
                    successCount++;
                } else {
                    errorCount++;
                }
            } catch (error) {
                console.error(`Error removing user ${userId}:`, error);
                errorCount++;
            }
        }

        toast.dismiss(loadingToast);

        if (errorCount === 0) {
            toast.success(`Successfully marked ${successCount} users for removal.`);
            onSuccess();
            onClose();
        } else {
            toast.warning(
                `Processed ${successCount} users. Failed to process ${errorCount} users. Please try again.`
            );
            // Optionally close if some succeeded, or keep open to retry failed ones.
            // For now, let's refresh list and close to be safe, or let user retry manually if we knew which ones failed.
            // A simple approach is to reload the page/data via onSuccess and close.
            onSuccess();
            onClose();
        }
        setSubmitting(false);
    };

    const remainingCount = requiredRemovalCount - selectedUserIds.length;
    const isValid = selectedUserIds.length === requiredRemovalCount;
    const isBelowMinSeatCount = newSeatCount < MIN_SEAT_COUNT;

    return (
        <Dialog open={open} handler={onClose} className="dialog">
            <DialogHeader className="dialog__header">Action Required: Seat Reduction</DialogHeader>
            <DialogBody className="dialog__body">
                {isBelowMinSeatCount && (
                    <Alert color="red" className="mb-4">
                        <Typography className="font-body text-white">
                            <strong>Error:</strong> Seat count cannot be reduced below {MIN_SEAT_COUNT}.
                            Please contact Gobby support to manage your subscription.
                        </Typography>
                    </Alert>
                )}
                <Typography className="mb-4 font-body text-black">
                    On <strong>{effectiveDate}</strong>, your subscription will be updated to{" "}
                    <strong>{newSeatCount}</strong> seats. Please select{" "}
                    <strong>{requiredRemovalCount}</strong> team members to deactivate.
                </Typography>
                <Typography className="mb-2 font-body text-black">
                    Selected: {selectedUserIds.length} / {requiredRemovalCount}
                    {remainingCount > 0 && ` (Select ${remainingCount} more)`}
                    {remainingCount < 0 && ` (Deselect ${Math.abs(remainingCount)})`}
                </Typography>

                <List className="p-0">
                    {users.map((user) => (
                        <ListItem key={user.id} className="p-0">
                            <label
                                htmlFor={`user-${user.id}`}
                                className="flex w-full cursor-pointer items-center px-3 py-2"
                            >
                                <ListItemPrefix className="mr-3">
                                    <Checkbox
                                        id={`user-${user.id}`}
                                        ripple={false}
                                        className="hover:before:opacity-0"
                                        containerProps={{
                                            className: "p-0",
                                        }}
                                        checked={selectedUserIds.includes(user.id)}
                                        onChange={() => handleUserToggle(user.id)}
                                    />
                                </ListItemPrefix>
                                <Typography color="blue-gray" className="font-medium">
                                    {user.name} ({user.email})
                                </Typography>
                            </label>
                        </ListItem>
                    ))}
                </List>
            </DialogBody>
            <DialogFooter className="dialog__footer">
                <Button
                    variant="text"
                    color="red"
                    onClick={onClose}
                    className="mr-1"
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    className="btn btn--primary"
                    onClick={handleSubmit}
                    disabled={!isValid || submitting || isBelowMinSeatCount}
                >
                    {submitting ? "Processing..." : "Confirm Deactivation"}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default PendingDowngradeDialog;
